<template>
  <div class="home home--cc container-fluid">
    <HomeDocumentationCollection
        v-if="home.audience === 'data_collection'"
        :home="home"
        :loading="loadingHome"
        @open-task="openTaskRow"
        @changed="refreshHomeSoon"
    />
    <HomeCommandCentre
        v-else
        :home="home"
        :validations="validationRows"
        :loading="loadingHome"
        :canCreateProject="!!(getClient && getClient.projectModule && isGranted(permissions.PROJECT_CREATE))"
        :canCreatePartition="!!(getClient && !getClient.parent && isGranted(permissions.PARTITION_CREATE))"
        :canInviteUsers="canInviteUsers"
        :canImportMeasures="!!(getClient && getClient.parent && !measuresFound && isGranted(permissions.MAINTENANCE_VIEW))"
        :showProjects="!!(getClient && getClient.projectModule && (navigation.project || isGranted(permissions.PROJECT_VIEW)))"
        @open-task="openTaskRow"
        @open-validation="openValidation"
        @open-project-tasks="openViewProjectModal"
        @create-project="$refs.createProject.showCreateProjectModal()"
        @create-partition="$refs.createPartition.showCreatePartitionModal()"
        @invite-user="$refs.inviteUser.showInviteModal()"
        @remind="sendReminders"
    />

    <!-- Create actions from the hero; the buttons of these components are not shown -->
    <span style="display:none;">
      <CreateProjectModal
          v-if="getClient && getClient.projectModule && isGranted(permissions.PROJECT_CREATE)"
          ref="createProject"
          @reloadContent="reloadContent()"
      />
      <PartitionFormModal
          v-if="getClient && !getClient.parent && isGranted(permissions.PARTITION_CREATE)"
          ref="createPartition"
          buttonType="none"
          switchAfterCreate
      />
      <UserInviteModal
          v-if="canInviteUsers"
          ref="inviteUser"
          buttonType="none"
      ></UserInviteModal>
    </span>

    <!-- LIST OF TASKS MODAL: my tasks in one project -->
    <b-modal
        ref="view-project"
        @hide="hideViewProjectModal"
        size="xl"
        modal-class="task-modal home-tm-modal"
        hide-header
        hide-footer
    >
      <div class="home-tm">
        <header class="home-tm__head">
          <div class="grow">
            <div class="home-tm__eyebrow">{{ $t('projects.tasks_modal.title') }}</div>
            <h2 class="home-tm__title">
              <router-link
                  v-if="isOwner(selectedProject)"
                  :to="{ name: 'projects.view', params: { slug: selectedProject.slug }}"
                  qid="s-view-page-link"
              >
                {{ selectedProject.title }}
                <font-awesome-icon icon="up-right-from-square" class="home-tm__ext"/>
              </router-link>
              <span v-else>{{ selectedProject.title }}</span>
            </h2>
            <div class="home-tm__meta">
              <span v-if="selectedProject.scope_start_date || selectedProject.scope_end_date" data-qid="scope-period">
                <font-awesome-icon icon="calendar-alt"/>
                <span v-if="selectedProject.scope_start_date">{{ selectedProject.scope_start_date | formatDate }}</span><span v-else>-</span>
                {{ $t('system.to') }}
                <span v-if="selectedProject.scope_end_date">{{ selectedProject.scope_end_date | formatDate }}</span><span v-else>-</span>
              </span>
              <span>{{ $tc('home.work.open_count', tasksTotalRows, { count: tasksTotalRows }) }}</span>
            </div>
          </div>
          <button
              type="button"
              class="home-icon-btn"
              :title="$t('system.close')"
              qid="home-tm-close"
              @click="hideViewProjectModal"
          >
            <font-awesome-icon icon="times"/>
          </button>
        </header>

        <div class="home-tm__toolbar">
          <div v-if="selectedProject.type === 'control_assessment'" class="v2-seg">
            <span :class="{ on: selectedTaskType === 'doc_collection_tasks' }" qid="home-tm-data" @click="setTaskType('doc_collection_tasks')">{{ $t('tasks.modal.data_collection_task') }}</span>
            <span :class="{ on: selectedTaskType === 'main_tasks' }" qid="home-tm-main" @click="setTaskType('main_tasks')">{{ $t('tasks.modal.main_task') }}</span>
          </div>
          <span class="grow"></span>
          <b-form-checkbox
              v-model="taskLoaded"
              name="check-button"
              switch
              class="home-tm__switch"
              @input="loadProjectTasks(1, perPage)"
          >
            {{ $t('tasks.modal.filter_completed') }}
          </b-form-checkbox>
        </div>

        <div class="home-tm__list">
          <div
              v-for="(object, index) in open_tasks"
              :key="'tm-' + index"
              class="home-tm__row"
              :class="{ 'is-done': isTaskRowCompleted(object) }"
          >
            <span class="home-tm__status" v-b-tooltip.hover :title="isTaskRowCompleted(object) ? $t('system.completed') : $t('system.pending')">
              <font-awesome-icon :icon="isTaskRowCompleted(object) ? 'check-circle' : 'clock'"/>
            </span>

            <div class="home-tm__main">
              <div class="home-tm__name">
                <span v-if="object.code === 'update_measures'">{{ $t('projects.measures.system_description_title') }}</span>
                <span v-else-if="object.code === 'business_risk_assessment' || object.code === 'privacy_risk_assessment' || object.code === 'system_impact_assessment'">{{ $t('maintenance.assessments.system_impact_assessment_label') }}</span>
                <span v-else-if="selectedProject.type === 'update' || selectedProject.type === 'validation'">{{ $te('tasks.values.' + $options.filters.slugify(object.title)) ? $t('tasks.values.' + $options.filters.slugify(object.title)) : object.title }}</span>
                <span v-else-if="object.reference">{{ object.reference }}: {{ object.title }}</span>
                <span v-else>{{ object.title }}</span>
              </div>
              <div class="home-tm__people">
                <span v-if="object.model && object.model.name" class="home-tm__asset">{{ object.model.name }}</span>
                <Avatar
                    v-for="(user, userIndex) in (selectedTaskType === 'doc_collection_tasks' && selectedProject.type === 'control_assessment' ? object.data_users : object.users)"
                    :key="'u-' + userIndex"
                    :object="user"
                    size="xs"
                />
              </div>
            </div>

            <div class="home-tm__actions">
              <button
                  v-if="!isTaskRowCompleted(object) && (selectedTaskType === 'main_tasks' || object.status !== 'completed')"
                  type="button"
                  class="home-btn home-btn--ghost"
                  qid="re-assign task"
                  @click="showEditUsersModal(object)"
              >
                <font-awesome-icon icon="user-tag"/>{{ $t('tasks.re_assign_button') }}
              </button>

              <AssessmentTaskModal
                  :task="object"
                  :tasks="selectedProject"
                  :fromHomepage="true"
                  @reloadContent="loadProjectTasks()"
                  v-if="selectedProject.type === 'bia_assessment' && canOpenRow(object)"
              ></AssessmentTaskModal>
              <AssessmentGlobalSingleTaskModal
                  :task="object"
                  :tasks="selectedProject"
                  :fromHomepage="true"
                  :panel="isEngagement(selectedProject) || selectedProject.type === 'risk_assessment'"
                  :selectedTaskType="selectedTaskType"
                  @reloadContent="loadProjectTasks(tasksCurrentPage)"
                  v-if="(selectedProject.type === 'control_assessment' || selectedProject.type === 'risk_assessment') && canOpenRow(object)"
              ></AssessmentGlobalSingleTaskModal>
              <RiskTaskModal
                  :task="object"
                  :tasks="selectedProject"
                  :fromHomepage="true"
                  @reloadContent="loadProjectTasks()"
                  v-if="(selectedProject.type === 'control' || selectedProject.type === 'security_measure') && canOpenRow(object)"
              ></RiskTaskModal>
              <UpdateTaskModal
                  :task="object"
                  :tasks="selectedProject"
                  :fromHomepage="true"
                  @reloadContent="loadProjectTasks()"
                  v-if="selectedProject.type === 'update' && canOpenRow(object)"
              ></UpdateTaskModal>
              <ValidationTaskModal
                  :task="object"
                  :tasks="selectedProject"
                  :fromHomepage="true"
                  @reloadContent="loadProjectTasks()"
                  v-if="selectedProject.type === 'validation' && canOpenRow(object)"
              ></ValidationTaskModal>

              <button
                  v-if="isTaskRowCompleted(object)"
                  type="button"
                  class="home-btn home-btn--ghost"
                  qid="view task"
                  @click="showViewTaskModal(object)"
              >
                <font-awesome-icon icon="eye"/>{{ $t('tasks.view_button') }}
              </button>
            </div>
          </div>

          <div v-if="open_tasks && !open_tasks.length && !loadingTasks" data-qid="no-results-row" class="home-empty">
            {{ $t('system.no_results') }}
          </div>
          <div v-if="loadingTasks" class="home-empty">
            <div class="home-skeleton"></div><div class="home-skeleton"></div><div class="home-skeleton home-skeleton--short"></div>
          </div>
        </div>

        <footer class="home-tm__foot">
          <div class="home-tm__per-page">
            <multiselect
                v-model="tasksPerPage"
                :options="perPageOptions"
                track-by="value"
                label="value"
                select-label=""
                selected-label=""
                deselect-label=""
                qid="home-tm-per-page"
                @select="tasksPerPageChanged"
                :placeholder="tasksSelectedPerPage"
                @remove="tasksPerPageRemoved"
            ></multiselect>
          </div>
          <span class="grow"></span>
          <b-pagination
              v-if="tasksRows > tasksSelectedPerPage"
              v-model="tasksCurrentPage"
              :total-rows="tasksRows"
              :per-page="tasksSelectedPerPage"
              aria-controls="my-table-1"
              @change="tasksPageChanged"
              limit="3"
              class="mb-0"
          ></b-pagination>
          <button
              type="button"
              class="home-btn"
              qid="home-tm-close-footer"
              @click="hideViewProjectModal">{{ $t('system.close') }}</button
          >
        </footer>
      </div>
    </b-modal>



    <!-- RE-ASSIGN MODAL -->
    <b-modal ref="re-assign" @hide="hideEditUsersModal" modal-class="task-modal">
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideEditUsersModal" class="btn btn-secondary cursor-pointer float-right">
            <font-awesome-icon icon="times"/>
          </span>

          <div class="d-block text-center">
            <h4 data-qid="re_assign-users-title">
              {{ $t('tasks.re_assign.title') }}
            </h4>
          </div>
        </div>
      </template>

      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="re_assign-users-error"
      >
        {{ error }}
      </b-alert>

      <b-form @submit="reassignUsers" qid="re_assign-users-form" v-if="selectedTask && selectedTask.model">
        <div class="form-container">
          <b-form-group>
            <span v-if="isOwner(selectedTask) && selectedTaskType === 'main_tasks'">
              <label>
                {{ $t('tasks.table.task_responsible') }}
              </label>

              <div class="multiselect-row">
                <multiselect
                    v-model="selectedAssignUsers"
                    :options="allUsers"
                    :disabled="buttonDisabled"
                    :multiple="true"
                    :taggable="false"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    track-by="slug"
                    :placeholder="$t('projects.create.owners_placeholder')"
                    label="name"
                    qid="re_assign-users-select"
                >
                  <template slot="tag" slot-scope="props">
                    <span class="mr-3 user-tag">
                      <Avatar :object="props.option" size="xs"></Avatar>
                      <span class="ml-2">
                        {{ props.option.name }}
                      </span>
                    </span>
                  </template>
                  <template slot="option" slot-scope="props">
                    <Avatar :object="props.option" size="xs"></Avatar>
                    <span class="ml-2">
                      {{ props.option.name }}
                    </span>
                  </template>
                </multiselect>

                <UserInviteModal
                    :buttonType="'small'"
                    :buttonDisabled=buttonDisabled
                    @reloadUserList="reloadUserList"
                ></UserInviteModal>
              </div>
              <br>
            </span>

            <span v-if="isDataOwner(selectedTask) && selectedTaskType === 'doc_collection_tasks'">
              <label data-qid="re_assign-data-users-label">
                {{ $t('tasks.table.data_collection_assignee') }}
              </label>

              <div class="multiselect-row">
                <multiselect
                    v-model="selectedAssignDataUsers"
                    :options="allUsers"
                    :disabled="buttonDisabled"
                    :multiple="true"
                    :taggable="false"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    track-by="slug"
                    :placeholder="$t('projects.create.owners_placeholder')"
                    label="name"
                    qid="re_assign-users-select"
                >
                  <template slot="tag" slot-scope="props">
                      <span class="mr-3 user-tag">
                        <Avatar :object="props.option" size="xs"></Avatar>
                        <span class="ml-2">
                          {{ props.option.name }}
                        </span>
                      </span>
                  </template>
                  <template slot="option" slot-scope="props">
                    <Avatar :object="props.option" size="xs"></Avatar>
                    <span class="ml-2">
                        {{ props.option.name }}
                      </span>
                  </template>
                </multiselect>

                <UserInviteModal
                    :buttonType="'small'"
                    :buttonDisabled=buttonDisabled
                    @reloadUserList="reloadUserList"
                ></UserInviteModal>
              </div>
            </span>
          </b-form-group>
        </div>
      </b-form>

      <template #modal-footer="{ close }">
        <div class="" style="width: 100%;">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">
              </div>
              <div class="col-sm-6 text-right">
                <b-button class="mt-3"
                          variant="secondary"
                          @click="hideEditUsersModal"
                          qid="re_assign-users-cancel-button"
                >
                  {{ $t('projects.create.cancel') }}
                </b-button>
                <b-button type="submit"
                          class="mt-3 ml-3"
                          variant="success"
                          :disabled="buttonDisabled"
                          @click="reassignUsers"
                          qid="re_assign-users-submit-button"
                >
                  <span v-if="buttonDisabled" class="mr-1">
                    <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                  </span>
                  {{ $t('projects.create.submit') }}
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </b-modal>

    <AssessmentTaskModal
        ref="routeAssessmentTaskModal"
        :task="selectedTask"
        :tasks="selectedTask && selectedTask.project ? selectedTask.project : {}"
        :fromHomepage="true"
        @reloadContent="reloadContent()"
        style="display:none;"
    />
    <AssessmentGlobalSingleTaskModal
        ref="routeAssessmentGlobalTaskModal"
        :task="selectedTask"
        :tasks="selectedTask && selectedTask.project ? selectedTask.project : {}"
        :fromHomepage="true"
        :panel="isEngagement(selectedTask && selectedTask.project) || !!(selectedTask && selectedTask.project && selectedTask.project.type === 'risk_assessment')"
        style="display:none;"
    />
    <RiskTaskModal
        ref="routeRiskTaskModal"
        :task="selectedTask"
        :tasks="selectedTask && selectedTask.project ? selectedTask.project : {}"
        :fromHomepage="true"
        @reloadContent="reloadContent()"
        style="display:none;"
    />
    <UpdateTaskModal
        ref="routeUpdateTaskModal"
        :task="selectedTask"
        :tasks="selectedTask && selectedTask.project ? selectedTask.project : {}"
        :fromHomepage="true"
        @reloadContent="reloadContent()"
        style="display:none;"
    />
    <ValidationTaskModal
        ref="routeValidationTaskModal"
        :task="selectedTask"
        :tasks="selectedTask && selectedTask.project ? selectedTask.project : {}"
        :fromHomepage="true"
        @reloadContent="reloadContent()"
        style="display:none;"
    />

    <TaskViewModal :selectedTask="selectedTask" :showModal="viewTaskModal" @closeViewModal="hideViewTaskModal" />
    <StatementTaskModal ref="statementTaskModal" @changed="refreshHomeSoon"/>
  </div>
</template>

<script>

import piincHttp from '@/util/configureAxios.js';
import { restoreRail } from '@/util/sidebarRail.js';
import roleCheck from '@/util/ACL/roleCheck';
import Avatar from '@/components/Avatar.vue';
import AssessmentTaskModal from '@/views/Tasks/AssessmentTaskModal.vue';
import AssessmentGlobalSingleTaskModal from '@/views/Tasks/AssessmentGlobalSingleTaskModal.vue';
import { isEngagement } from '@/views/Projects/Engagement/engagement.js';
import RiskTaskModal from '@/views/Tasks/RiskTaskModal.vue';
import UpdateTaskModal from '@/views/Tasks/UpdateTaskModal.vue';
import ValidationTaskModal from '@/views/Tasks/ValidationTaskModal.vue';
import TaskViewModal from '@/views/Tasks/TaskViewModal.vue';
import StatementTaskModal from '@/views/Home/StatementTaskModal.vue';
import CreateProjectModal from '@/views/Projects/CreateProjectModal.vue';
import UserInviteModal from '@/components/user/UserInviteModal.vue';
import PartitionFormModal from '@/components/partition/PartitionFormModal.vue';
import HomeCommandCentre from '@/views/Home/HomeCommandCentre.vue';
import HomeDocumentationCollection from '@/views/Home/HomeDocumentationCollection.vue';
import { validationRow, validationRoute } from '@/views/Home/homeTasks.js';

export default {
  name: 'Home',
  data() {
    return {
      navigation: [],
      allUsers: [],
      error: '',
      selectedTask: [],
      viewTaskModal: false,
      selectedAssignUsers: [],
      selectedAssignDataUsers: [],
      buttonDisabled: false,
      loading: false,
      selectedProject: {},
      open_tasks: [],
      loadingTasks: false,

      // Home (US 9373)
      home: {},
      loadingHome: true,
      validations: [],
      homeRequest: '',
      homeReloadQueued: false,
      homeRefreshTimer: null,

      // Pagination of the list of tasks modal
      perPageOptions: [
        { name: '10', value: '10' },
        { name: '20', value: '20' },
        { name: '50', value: '50' },
        { name: '100', value: '100' }
      ],
      perPage: '',
      tasksSelectedPerPage: '10',
      tasksCurrentPage: 1,
      tasksTotalRows: 0,
      tasksPerPage: '',

      taskLoaded: 0,
      selectedTaskType: '',
      measuresFound: true,
      routeTaskHandledKey: '',
      routeTaskOpening: false,
    };
  },
  components: {
    Avatar,
    AssessmentTaskModal,
    AssessmentGlobalSingleTaskModal,
    RiskTaskModal,
    UpdateTaskModal,
    ValidationTaskModal,
    TaskViewModal,
    StatementTaskModal,
    CreateProjectModal,
    UserInviteModal,
    PartitionFormModal,
    HomeCommandCentre,
    HomeDocumentationCollection,
  },
  watch: {
    // Watch when client changes in store and trigger api reload
    getClient: function () {
      this.trialExpiredRedirect()
      this.loadNavigation()
      this.loadHome()
      this.checkMeasuresIfPartition()
      this.openTaskFromRouteIfNeeded()
    },
    getReloadPage: function () {
      this.loadHome()
    },
    // A task was closed or completed (from Home, the sidebar or a task list): show its new state
    '$store.state.lastClosedTask': function () {
      this.refreshHomeSoon()
    },
    '$store.state.reloadTasks': function () {
      this.refreshHomeSoon()
    },
    getUser: function (newValue) {
      if (newValue && newValue.slug) {
        this.openTaskFromRouteIfNeeded()
      }
    },
    '$route.fullPath': function () {
      if (!this.$route.query.task_id) {
        this.routeTaskHandledKey = ''
      }
      this.openTaskFromRouteIfNeeded()
    },
  },
  computed: {
    // Client and partition admins can invite users from Home
    canInviteUsers() {
      return !!(this.getClient && this.getClient.slug && (roleCheck('Client admin') || roleCheck('Partition admin') || (this.getUser && this.getUser.isAdmin)));
    },
    getClient() {
      return this.$store.state.client
    },
    getUser() {
      return this.$store.state.user
    },
    getReloadPage() {
      return this.$store.state.updatePage
    },
    getStorePerPage() {
      return this.$store.state.perPage
    },
    tasksRows() {
      return this.tasksTotalRows
    },
    validationRows() {
      const today = this.home.today;
      return this.validations.map(validation => validationRow(validation, today));
    },
  },
  methods: {
    loadHome() {
      if (!this.getClient || !this.getClient.slug) {
        return;
      }
      // The page loads on create and again when the client arrives in the store: one request per client is enough
      // A reload asked for meanwhile runs once that request is done, so changes saved in between are shown
      const client = this.getClient.slug;
      if (this.homeRequest === client) {
        this.homeReloadQueued = true;
        return;
      }
      this.homeRequest = client;
      this.homeReloadQueued = false;
      this.loadingHome = !this.home.tasks;
      piincHttp.get('home', { params: { client } }).then(response => {
        this.homeRequest = '';
        if (this.homeReloadQueued) {
          this.loadHome();
        }
        if (!this.getClient || this.getClient.slug !== client) {
          return;
        }
        this.home = response.data;
        this.loadingHome = false;
        if (this.home.audience !== 'data_collection') {
          this.loadValidations();
        }
      }, () => {
        this.homeRequest = '';
        this.loadingHome = false;
      });
    },
    // The task modals save as they close, so Home reloads just after
    refreshHomeSoon() {
      clearTimeout(this.homeRefreshTimer);
      this.homeRefreshTimer = setTimeout(() => this.loadHome(), 600);
    },
    loadValidations() {
      piincHttp.get('validations/rejected', { params: { page: 1, per_page: 20, client: this.getClient.slug } }).then(response => {
        this.validations = response.data.items || [];
      }, () => {});
    },
    reloadContent() {
      this.loadHome()
      this.$store.commit('storeReloadCount');
    },
    // Open a task from Home in its own modal (or the engagement task panel)
    openTaskRow(row) {
      // The management statement or system description sent to the user (US 9394)
      if (row.kind === 'statement') {
        this.$refs.statementTaskModal.open(row.statement_id);
        return;
      }
      piincHttp.post('home/tasks/' + row.id + '/opened').then(() => {}, () => {});
      piincHttp.get('tasks/' + row.id).then(response => {
        const task = response.data;
        if (!task || !task.id) {
          return;
        }
        this.selectedTask = task;
        this.selectedTaskType = row.kind === 'data' ? 'doc_collection_tasks' : 'main_tasks';
        this.$nextTick(() => {
          this.openDeepLinkedTaskModal(task);
        });
      }, () => {});
    },
    openValidation(row) {
      const route = validationRoute(row.validation);
      if (route) {
        this.$router.push(route);
      }
    },
    sendReminders(project) {
      piincHttp.get('projects/' + project.slug + '/reminders').then(() => {
        this.$toastr('success', this.$t('projects.create.project_reminders_success'));
      }, () => {});
    },
    setTaskType(type) {
      this.selectedTaskType = type;
      this.onTaskTypeChange();
    },
    isTaskRowCompleted(object) {
      return (this.selectedTaskType === 'main_tasks' && object.status === 'completed') ||
        (this.selectedTaskType === 'doc_collection_tasks' && object.data_status === 'completed');
    },
    // Same rule as before: pending main tasks, or the open documentation collection for its assignees
    canOpenRow(object) {
      return (object.status === 'pending' && !this.isDataUser(object)) ||
        ((object.data_status === 'pending' || !object.data_status) && this.isDataUser(object));
    },
    hideViewProjectModal() {
      this.selectedProject = {};
      this.open_tasks = [];
      this.tasksPerPage = '';
      this.tasksCurrentPage = 1;
      this.tasksTotalRows = 0;

      this.$refs['view-project'].hide();

      this.loadHome()
    },
    checkMeasuresIfPartition() {
      if (this.getClient && this.getClient.parent) {
        this.checkMeasures();
      }
    },
    isEngagement(project) {
      return isEngagement(project);
    },
    isOwner(object) {
      let self = this;
      let allow = false;
      let existingUser = _.find(object.users, function(user) {
        return user.id === self.getUser.id;
      });

      if (existingUser) {
        allow = true;
      } else {
        if (roleCheck('Client admin') || roleCheck('Partition admin') || roleCheck('Validator') || this.getUser.isAdmin) {
          allow = true;
        }
      }

      return allow;
    },
    isDataOwner(object) {
      let self = this;
      return _.find(object.data_users, function (user) {
        return user.id === self.getUser.id;
      });
    },
    isDataUser(object) {
      let self = this;
      let existingUser = _.find(object.data_users, function(user) {
        return user.id === self.getUser.id;
      });

      let existingTaskUser = _.find(object.users, function(user) {
        return user.id === self.getUser.id;
      });

      if(existingTaskUser){
        return false;
      }

      return !!existingUser;
    },
    tasksPageChanged(page) {
      this.loadProjectTasks(page)
    },
    tasksPerPageChanged(perPage) {
      let self = this;
      self.tasksSelectedPerPage = perPage.value
      this.$store.commit('storePerPage', this.tasksSelectedPerPage);
      // Load content when page changed listener is triggered
      this.loadProjectTasks(1, perPage.value)
    },
    tasksPerPageRemoved() {
      let self = this;
      // Load content when page changed listener is triggered
      self.tasksSelectedPerPage = self.perPageOptions[0].value
      this.loadProjectTasks(1, self.tasksSelectedPerPage)
    },
    showViewTaskModal(object) {
      this.selectedTask = object
      this.viewTaskModal = true
    },
    hideViewTaskModal() {
      this.selectedTask = {}
      this.viewTaskModal = false
    },
    getRouteTaskType(type) {
      return type === 'doc_collection_tasks' ? 'doc_collection_tasks' : 'main_tasks'
    },
    openDeepLinkedTaskModal(task) {
      if (!task || !task.project || !task.project.type) {
        return
      }

      let refName = ''

      // Risk tasks open as in the project, which knows their drop-down options (US 9478)
      if (task.project.type === 'bia_assessment') {
        refName = 'routeAssessmentTaskModal'
      } else if (task.project.type === 'control_assessment' || task.project.type === 'risk_assessment') {
        refName = 'routeAssessmentGlobalTaskModal'
      } else if (task.project.type === 'control' || task.project.type === 'security_measure') {
        refName = 'routeRiskTaskModal'
      } else if (task.project.type === 'update') {
        refName = 'routeUpdateTaskModal'
      } else if (task.project.type === 'validation') {
        refName = 'routeValidationTaskModal'
      }

      if (refName && this.$refs[refName] && typeof this.$refs[refName].showTaskModal === 'function') {
        this.$refs[refName].showTaskModal(task)
      } else {
        this.showViewTaskModal(task)
      }
    },
    openTaskFromRouteIfNeeded() {
      const taskId = this.$route.query.task_id

      if (!taskId || !this.getClient || !this.getClient.slug || !this.getUser || !this.getUser.id) {
        return
      }

      const routeTaskKey = `${this.getClient.slug}:${taskId}:${this.getRouteTaskType(this.$route.query.type)}`
      if (this.routeTaskOpening || this.routeTaskHandledKey === routeTaskKey) {
        return
      }

      this.routeTaskOpening = true

      piincHttp.get('tasks/' + taskId).then((response) => {
        const task = response.data

        if (!task || !task.id) {
          this.routeTaskOpening = false
          return
        }

        this.selectedTask = task
        this.selectedTaskType = this.getRouteTaskType(this.$route.query.type)
        this.routeTaskHandledKey = routeTaskKey

        this.$nextTick(() => {
          this.openDeepLinkedTaskModal(task)
          this.routeTaskOpening = false
        })
      }, () => {
        this.routeTaskOpening = false
      })
    },
    hideEditUsersModal() {
      this.selectedTask = {};
      this.selectedAssignUsers = [];
      this.selectedAssignDataUsers = [];
      this.$refs['re-assign'].hide();
    },
    reloadUserList() {
      let object = this.selectedTask

      this.$refs['re-assign'].hide();
      this.showEditUsersModal(object);
    },
    showEditUsersModal(object) {
      let self = this;
      let task = object;
      let role_name = [self.permissions.ROLE_CLIENT_ADMIN];

      this.error = '';
      this.buttonDisabled = true;
      this.loading = true;

      piincHttp.get('tasks/' + object.id).then(function(response) {
        task = response.data;

        if (task.project.type === 'control' || task.project.type === 'security_measure' || task.project.type === 'control_assessment') {
          role_name.push(self.permissions.ROLE_MEMBER);
          role_name.push(self.permissions.ROLE_VALIDATOR);
        }

        if (self.getClient && self.getClient.parent) {
          role_name.push(self.permissions.ROLE_PARTITION_ADMIN);
        }

        self.selectedAssignUsers = [];
        self.selectedAssignDataUsers = [];

        const params = {
          client: self.getClient.id,
          per_page: 100
        };

        if (task.code !== 'control_assessment') {
          params.role_name = role_name;
        }

        piincHttp.get('users', { params }).then(function(response) {
          let users = response.data.items;

          _.forEach(object.users, function (user) {
            if (_.findIndex(users, function(o) { return o.id === user.id; }) === -1) {

              users.push(user);

            }

            let matchedUser = _.cloneDeep(user);
            if (user.id === object.owner) {
              matchedUser.owner = true;
            }
            self.selectedAssignUsers.push(matchedUser);

          })

          _.forEach(object.data_users, function (user) {
            if (_.findIndex(users, function(o) { return o.id === user.id; }) === -1) {

              users.push(user);

            }

            let matchedUser = _.cloneDeep(user);
            if (user.id === object.owner) {
              matchedUser.owner = true;
            }
            self.selectedAssignDataUsers.push(matchedUser);

          })

          self.allUsers = _.uniq(users);
          self.buttonDisabled = false;
          self.loading = false;
        }, function() {
          self.buttonDisabled = false;
          self.loading = false;
        });
      }, function() {
        self.buttonDisabled = false;
        self.loading = false;
      });

      this.selectedTask = object;
      this.$refs['re-assign'].show();
    },
    reassignUsers(evt) {
      evt.preventDefault();

      this.buttonDisabled = true;

      if (this.isOwner(this.selectedTask)) {
        this.reassignFormSubmit();

        if (this.isDataOwner(this.selectedTask)) {
          this.reassignDataFormSubmit();
          this.hideEditUsersModal();
          // this.loadProjectTasks();
        } else {
          this.hideEditUsersModal();
          // this.loadProjectTasks();
        }
      } else {
        if (this.isDataOwner(this.selectedTask)) {
          this.reassignDataFormSubmit();
          this.hideEditUsersModal();
          // this.loadProjectTasks();
        } else {
          this.hideEditUsersModal();
          // this.loadProjectTasks();
        }
      }

      this.buttonDisabled = false;
    },
    reassignFormSubmit() {
      //this.buttonDisabled = true;
      // Keep the reference in a variable
      // to use it inside function called when promise returned
      let self = this;
      let userIds = [];

      if (this.selectedAssignUsers && this.selectedAssignUsers.length) {
        for (var i = 0; i < this.selectedAssignUsers.length; i++) {
          userIds.push(this.selectedAssignUsers[i].id);
        }
      }

      if (userIds && userIds.length) {
        piincHttp.put('tasks/' + this.selectedTask.id, {
          users: userIds,
          reasign: 'replace'
        }).then(function() {
          // self.hideEditUsersModal();
          self.loadProjectTasks();
          self.$toastr('success', self.$t('tasks.re_assign.success'))
          // self.buttonDisabled = false;
        }, function(error) {
          if (error.status !== 200) {
            // self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })
      } else {
        self.error = self.$t('tasks.re_assign.required')
        //self.buttonDisabled = false;
      }
    },
    reassignDataFormSubmit() {
      //this.buttonDisabled = true;
      // Keep the reference in a variable
      // to use it inside function called when promise returned
      let self = this;
      let userIds = [];

      if (this.selectedAssignDataUsers && this.selectedAssignDataUsers.length) {
        for (var i = 0; i < this.selectedAssignDataUsers.length; i++) {
          userIds.push(this.selectedAssignDataUsers[i].id);
        }
      }

      if (userIds && userIds.length) {
        piincHttp.put('tasks/' + this.selectedTask.id, {
          data_users: userIds,
          reasign: 'replace'
        }).then(function() {
          // self.hideEditUsersModal();
          self.loadProjectTasks();
          self.$toastr('success', self.$t('tasks.re_assign.success'))
          // self.buttonDisabled = false;
        }, function(error) {
          if (error.status !== 200) {
            // self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })
      } else {
        self.error = self.$t('tasks.re_assign.required')
        //self.buttonDisabled = false;
      }
    },
    loadProjectTasks(page, perPage, sort = 'order', order = 'asc') {
      let self = this;

      // Without an open project list (e.g. re-assigning a task opened from Home) only Home is refreshed
      if (!this.selectedProject || !this.selectedProject.slug) {
        this.loadHome();
        return;
      }
      this.loadingTasks = true;

      let status = ['pending']
      if(this.taskLoaded){
        status = ['pending','completed']
      }

      if (this.getStorePerPage) {
        this.tasksSelectedPerPage = this.getStorePerPage;
      }

      // If page is not defined, set page to default 1
      let perPageNumber = perPage ? perPage : this.tasksSelectedPerPage;

      let pageNumber = 1; //page ? page : 1;
      if (page) {
        pageNumber = page ? page : 1;
        this.tasksCurrentPage = page;
      }else{
        pageNumber = 1;
        this.tasksCurrentPage = 1;
      }

      piincHttp.get('projects/' + this.selectedProject.slug + '/tasks/me/small', { params:
            {
              page: pageNumber,
              per_page: perPageNumber,
              status: status,
              sort: sort,
              order: order,
              type: this.selectedTaskType
            }
      }).then(function(response) {
        // self.open_tasks = []
        self.open_tasks = response.data.items;
        // Set number of total items
        self.tasksTotalRows = response.data.total_count;
        self.loadingTasks = false;
      }, function() {});
    },
    openViewProjectModal(object) {
      this.selectedProject = object;

      // On control assessments start with the kind of tasks the user has in the project (main tasks for auditors)
      const myKinds = (this.home.tasks || []).filter(task => task.project && task.project.id === object.id).map(task => task.kind);
      if (this.selectedProject.type === 'control_assessment' && !myKinds.includes('main')) {
        this.selectedTaskType = 'doc_collection_tasks';
      } else {
        this.selectedTaskType = 'main_tasks'
      }

      this.loadProjectTasks();
      this.$refs['view-project'].show();
    },
    trialExpiredRedirect() {
      var moment = require('moment');
      let self = this;

      setTimeout(function(){
        let currentDate = moment()
        let endDate = moment(self.getClient.trial_ends_at);
        let difference = endDate.diff(currentDate, 'days');

        if ((self.getClient.subscription_status === 'trial_expired' && difference < 1) || self.getClient.subscription_status === 'subscription_ended') {
          self.$router.push({ name: 'clientProfile', params: { slug: self.getClient.slug }, query: { tabindex: 1 }})
        }
      }, 500);

    },
    loadNavigation() {
      let self = this;
      piincHttp.get('users/me/navigation', { params:
            {
              client: this.getClient.slug
            }
      }).then(function (response) {
        self.navigation = response.data;
      }, function () {});
    },
    onTaskTypeChange() {
      this.loadProjectTasks(1, this.tasksPerPage, 'order', 'asc');
    },
    checkMeasures() {
      let self = this;

      self.measuresFound = true;

      piincHttp.get('measure-categories', { params:
            {
              per_page: 1,
              client: self.getClient.slug
            }
      }).then(function(categories_response) {
        if (categories_response.data.total_count) {
          self.measuresFound = true;
        }else{
          piincHttp.get('measures', { params:
                {
                  page: 1,
                  per_page: 1,
                  client: self.getClient.slug
                }
          }).then(function(measure_response) {
            self.measuresFound = !!measure_response.data.total_count;
          }, function() {});
        }
      }, function() {});
    },
  },
  created() {
    if (this.getClient && this.getClient.slug) {
      this.trialExpiredRedirect()
      this.loadNavigation()
      this.loadHome()
      this.checkMeasuresIfPartition()
    }
    this.openTaskFromRouteIfNeeded()
  },
  mounted () {
    // Show the sidebar the way the user left it (US 9372)
    restoreRail();
  },
  beforeDestroy() {
    clearTimeout(this.homeRefreshTimer);
  }
}

</script>
